import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";

/* ---------------------------------------------------------------- Switch */

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Accessible name when there is no visible <label htmlFor>. */
  label?: string;
  id?: string;
  disabled?: boolean;
  className?: string;
}

/** An immediate on/off setting. For a choice that is submitted later, use Checkbox. */
export function Switch({ checked, onCheckedChange, label, id, disabled, className }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cx("ui-switch", className)}
    />
  );
}

/* -------------------------------------------------------------- Checkbox */

export type CheckboxProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type">;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox({ className, ...rest }, ref) {
  return <input ref={ref} type="checkbox" className={cx("ui-checkbox", className)} {...rest} />;
});

/* ------------------------------------------------------ SegmentedControl */

export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<SegmentedOption<T>>;
  /** Names the group for assistive technology. */
  label: string;
  className?: string;
}

/**
 * Two to four mutually exclusive views. Implemented as a radio group, with
 * arrow-key movement, because that is what it is semantically.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  label,
  className,
}: SegmentedControlProps<T>) {
  const move = (from: number, step: number) => {
    const next = options[(from + step + options.length) % options.length];
    if (next) onValueChange(next.value);
  };

  return (
    <div role="radiogroup" aria-label={label} className={cx("ui-segmented", className)}>
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className="ui-segmented__item"
            onClick={() => onValueChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                event.preventDefault();
                move(index, 1);
              } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                event.preventDefault();
                move(index, -1);
              }
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------- QuantityStepper */

export interface QuantityStepperProps {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  /** The thing being counted, for the button labels ("Decrease quantity of …"). */
  itemLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** One bordered control: minus, the number, plus. */
export function QuantityStepper({
  value,
  onValueChange,
  min = 1,
  max = 99,
  itemLabel,
  disabled,
  className,
}: QuantityStepperProps) {
  const id = useId();
  const suffix = itemLabel ? ` of ${itemLabel}` : "";
  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  return (
    <div className={cx("ui-stepper", className)} role="group" aria-labelledby={id}>
      <span id={id} className="ui-sr-only">
        Quantity{suffix}
      </span>
      <button
        type="button"
        className="ui-stepper__btn"
        aria-label={`Decrease quantity${suffix}`}
        disabled={disabled || value <= min}
        onClick={() => onValueChange(clamp(value - 1))}
      >
        <MinusIcon />
      </button>
      <output className="ui-stepper__value" aria-live="polite">
        {value}
      </output>
      <button
        type="button"
        className="ui-stepper__btn"
        aria-label={`Increase quantity${suffix}`}
        disabled={disabled || value >= max}
        onClick={() => onValueChange(clamp(value + 1))}
      >
        <PlusIcon />
      </button>
    </div>
  );
}

function MinusIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
      <path d="M3.5 8h9" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" aria-hidden="true">
      <path d="M8 3.5v9M3.5 8h9" />
    </svg>
  );
}
